<template>
    <canvas ref="canvas" height="200" style="width: 100%;" />
</template>

<script>
function spline(ctx, pts, yKey) {
    ctx.moveTo(pts[0].x, pts[0][yKey])
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[Math.min(pts.length - 1, i + 2)]
        ctx.bezierCurveTo(
            p1.x + (p2.x - p0.x) / 6, p1[yKey] + (p2[yKey] - p0[yKey]) / 6,
            p2.x - (p3.x - p1.x) / 6, p2[yKey] - (p3[yKey] - p1[yKey]) / 6,
            p2.x, p2[yKey],
        )
    }
}

const PHASE_COLORS = {
    0: '#64748b',
    1: '#94a3b8',
    2: '#d97706',
    3: '#94a3b8',
}

export default {
    name: 'MoonPhaseChartTemp',
    props: {
        days: { type: Array, default: () => [] },
        phases: { type: Array, default: () => [] },
        title: { type: String, default: '' },
        subtitle: { type: String, default: '' },
        subtitle2: { type: String, default: '' },
    },
    watch: {
        days() { this.redraw() },
        phases() { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || !this.days.length) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP = 84
            const PAD_BOTTOM = 48
            const PAD_LEFT = 32
            const PAD_RIGHT = 40
            const plotW = w - PAD_LEFT - PAD_RIGHT
            const plotH = h - PAD_TOP - PAD_BOTTOM

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 40)
            }

            const n = this.days.length
            const toX = i => PAD_LEFT + (i / (n - 1)) * plotW
            const toY = pct => PAD_TOP + plotH - (pct / 100) * plotH
            const toYAngle = deg => PAD_TOP + plotH - (deg / 180) * plotH

            ctx.font = '10px system-ui, sans-serif'
            for (const pct of [25, 50, 75, 100]) {
                const y = toY(pct)
                ctx.beginPath()
                ctx.moveTo(PAD_LEFT, y)
                ctx.lineTo(PAD_LEFT + plotW, y)
                ctx.strokeStyle = 'rgba(0,0,0,0.07)'
                ctx.lineWidth = 1
                ctx.setLineDash([4, 4])
                ctx.stroke()
                ctx.setLineDash([])

                ctx.textAlign = 'right'
                ctx.fillStyle = 'rgba(180, 148, 70, 0.7)'
                ctx.fillText(pct + '%', PAD_LEFT - 8, y + 4)

                const deg = Math.round((pct / 100) * 180)
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(99, 130, 180, 0.7)'
                ctx.fillText(deg + '°', PAD_LEFT + plotW + 8, y + 4)
            }
            ctx.textAlign = 'left'
            ctx.fillStyle = 'rgba(99, 130, 180, 0.7)'
            ctx.fillText('180°', PAD_LEFT + plotW + 8, PAD_TOP + 4)
            ctx.fillText('0°', PAD_LEFT + plotW + 8, PAD_TOP + plotH + 4)

            const pts = this.days.map((d, i) => ({
                x: toX(i),
                y: toY(d.illumination_percentage),
                yAngle: toYAngle(d.phase_angle),
                day: parseInt(d.short_date.split('-')[2]),
            }))

            ctx.beginPath()
            spline(ctx, pts, 'y')
            ctx.lineTo(pts[pts.length - 1].x, toY(0))
            ctx.lineTo(pts[0].x, toY(0))
            ctx.closePath()
            const grad = ctx.createLinearGradient(0, PAD_TOP, 0, PAD_TOP + plotH)
            grad.addColorStop(0, 'rgba(203, 175, 100, 0.22)')
            grad.addColorStop(1, 'rgba(203, 175, 100, 0.03)')
            ctx.fillStyle = grad
            ctx.fill()

            ctx.beginPath()
            spline(ctx, pts, 'y')
            ctx.strokeStyle = 'rgba(180, 148, 70, 0.75)'
            ctx.lineWidth = 2
            ctx.stroke()

            ctx.beginPath()
            spline(ctx, pts, 'yAngle')
            ctx.strokeStyle = 'rgba(99, 130, 180, 0.65)'
            ctx.lineWidth = 2
            ctx.setLineDash([4, 4])
            ctx.stroke()
            ctx.setLineDash([])

            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'center'
            ctx.fillStyle = 'rgba(0,0,0,0.4)'
            for (let i = 0; i < n; i++) {
                const day = pts[i].day
                if (day === 1 || day % 5 === 0) {
                    ctx.fillText(day, pts[i].x, PAD_TOP + plotH + 16)
                }
            }

            const legendY = h - 8
            const lineLen = 16
            const gap = 8
            ctx.font = '11px system-ui, sans-serif'

            const label1 = 'Illum.%'
            const label2 = 'Phase°'
            const totalWidth = lineLen + gap + ctx.measureText(label1).width + 24 + lineLen + gap + ctx.measureText(label2).width
            let lx = (w - totalWidth) / 2

            ctx.beginPath()
            ctx.moveTo(lx, legendY - 4)
            ctx.lineTo(lx + lineLen, legendY - 4)
            ctx.strokeStyle = 'rgba(180, 148, 70, 0.75)'
            ctx.lineWidth = 2
            ctx.setLineDash([])
            ctx.stroke()
            lx += lineLen + gap
            ctx.textAlign = 'left'
            ctx.fillStyle = 'rgba(0,0,0,0.5)'
            ctx.fillText(label1, lx, legendY)
            lx += ctx.measureText(label1).width + 24

            ctx.beginPath()
            ctx.moveTo(lx, legendY - 4)
            ctx.lineTo(lx + lineLen, legendY - 4)
            ctx.strokeStyle = 'rgba(99, 130, 180, 0.65)'
            ctx.lineWidth = 2
            ctx.setLineDash([4, 4])
            ctx.stroke()
            ctx.setLineDash([])
            lx += lineLen + gap
            ctx.fillStyle = 'rgba(0,0,0,0.5)'
            ctx.fillText(label2, lx, legendY)

            ctx.font = '13px system-ui, sans-serif'
            ctx.textAlign = 'center'
            for (const event of this.phases) {
                const dayNum = parseInt(event.short_date.split('-')[2])
                const idx = this.days.findIndex(d => parseInt(d.short_date.split('-')[2]) === dayNum)
                if (idx === -1) continue
                const pt = pts[idx]
                const color = PHASE_COLORS[event.type] || '#94a3b8'

                ctx.beginPath()
                ctx.moveTo(pt.x, PAD_TOP)
                ctx.lineTo(pt.x, PAD_TOP + plotH)
                ctx.strokeStyle = color + '40'
                ctx.lineWidth = 1
                ctx.setLineDash([4, 4])
                ctx.stroke()
                ctx.setLineDash([])

                ctx.beginPath()
                ctx.arc(pt.x, pt.y, 4, 0, Math.PI * 2)
                ctx.fillStyle = '#fff'
                ctx.fill()
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.stroke()

                ctx.fillStyle = color
                ctx.fillText(event.symbol, pt.x, PAD_TOP - 16)
            }
        },
    },
}
</script>
